/**
 * bb-location-banner.js
 * ───────────────────────────────────────────────────────────────────────────
 * Location banner / confirm-ZIP nudge (spec §4 + §5).
 *
 * Reacts to `bb:location-resolved` and shows one of these states (keyed off the
 * `served` determination, NOT merely "region is null"):
 *   - SERVED + guessed (ipapi)            → SOFT nudge with the state name
 *   - SERVED + confirmed (manual ZIP)     → hidden (never nudge again)
 *   - KNOWN-UNSERVED (served === false)   → firm RED "we don't serve…" banner
 *   - UNKNOWN (served == null: transient geo failure / no detection) → SOFT
 *     fallback nudge, NOT the firm banner — a one-off rate-limit must never tell
 *     a served visitor we don't serve their area.
 *
 * SECURITY (§0.5): all dynamic text (incl. the state name) is written via
 * textContent — never innerHTML — so tampered data can't inject markup. Copy is
 * read from the banner's data-* attributes (authored in Liquid, localizable).
 *
 * The CTA opens the manual ZIP modal (Phase 5) by dispatching BB_OPEN_ZIP_MODAL;
 * until that exists the click is a harmless no-op.
 */

import { getStateName } from '@theme/bb-region-config';
import { BB_LOCATION_EVENT, getLastResolved } from '@theme/bb-geo-detect';

/** Dispatched on `document` when the visitor asks to enter/confirm their ZIP. */
export const BB_OPEN_ZIP_MODAL = 'bb:open-zip-modal';

const BANNER_ID = 'bb-location-banner';

/**
 * Re-sum #header-group's height into --header-group-height. The banner now lives
 * inside #header-group (see theme.liquid [R-04]) but the theme only computes this
 * var on load / resize — not when the banner reveals or hides. Without this, the
 * fixed BROWSE mega panel (top: var(--header-group-height)) would overlap the
 * banner. Mirrors the inline calc in theme.liquid. Best-effort: no-op if the
 * header group isn't present (e.g. password page).
 */
function syncHeaderGroupHeight() {
  if (typeof document === 'undefined') return;
  const group = document.getElementById('header-group');
  if (!group) return;
  const headerComponent = document.querySelector('header-component');
  let total = 0;
  for (const el of group.children) {
    if (el === headerComponent || !(el instanceof HTMLElement)) continue;
    total += el.offsetHeight;
  }
  document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
}

function getRefs() {
  const banner = document.getElementById(BANNER_ID);
  if (!banner) return null;
  return {
    banner,
    message: banner.querySelector('#bb-location-banner-message'),
    cta: banner.querySelector('#bb-location-banner-cta'),
  };
}

function hide(refs) {
  refs.banner.setAttribute('data-bb-state', 'hidden');
  refs.banner.setAttribute('hidden', '');
  syncHeaderGroupHeight();
}

/**
 * Reveal the banner, then set the message + CTA text on the next frame so the
 * aria-live region is already rendered when the text is inserted — toggling
 * visibility and content in the same tick is unreliably announced.
 */
function announce(refs, state, message, ctaLabel) {
  refs.banner.setAttribute('data-bb-state', state);
  refs.banner.removeAttribute('hidden');
  const apply = () => {
    refs.message.textContent = message;
    refs.cta.textContent = ctaLabel;
    // Banner is now visible with its final copy → fold its height into the header
    // group var so the BROWSE mega panel clears it.
    syncHeaderGroupHeight();
  };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(apply);
  else apply();
}

function showSoft(refs, stateName) {
  const { softPrefix = '', softSuffix = '', softFallback = '', softCta = '' } = refs.banner.dataset;
  const message = stateName ? `${softPrefix}${stateName}${softSuffix}` : softFallback;
  announce(refs, 'soft', message, softCta);
}

function showUnmapped(refs) {
  const { unmappedMessage = '', unmappedCta = '' } = refs.banner.dataset;
  announce(refs, 'unmapped', unmappedMessage, unmappedCta);
}

function render(detail) {
  const served = detail && typeof detail.served === 'boolean' ? detail.served : null;

  // Reflect status on the nav region pill's dot (moss = located/served, accent =
  // not). Unknown (served null) shows the accent dot — accurate ("not set yet") —
  // while the banner stays SOFT, so only a positively-unserved visitor sees red.
  if (typeof document !== 'undefined') {
    for (const trigger of document.querySelectorAll('.bb-zip-trigger')) {
      trigger.setAttribute('data-bb-region', served === true ? 'mapped' : 'unmapped');
    }
  }

  const refs = getRefs();
  if (!refs || !refs.message || !refs.cta) return;

  const confirmed = Boolean(detail && detail.zip_confidence === 'confirmed');

  if (served === false) {
    showUnmapped(refs); // KNOWN-unserved (D–I / AK-HI / non-US) → firm banner
  } else if (served === true && confirmed) {
    hide(refs); // confirmed in a served region → no nudge
  } else {
    // Served-but-guessed → "Showing plants for <State>". Unknown → soft fallback.
    showSoft(refs, getStateName(detail && detail.state));
  }
}

function boot() {
  if (typeof document === 'undefined') return;
  // Don't nudge merchants editing in the theme editor (no real visitor).
  if (window.Shopify && window.Shopify.designMode) return;

  const refs = getRefs();
  if (!refs || !refs.cta) return;

  refs.cta.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent(BB_OPEN_ZIP_MODAL));
  });

  document.addEventListener(BB_LOCATION_EVENT, (event) => render(event.detail));

  const last = getLastResolved();
  if (last !== undefined) render(last);
}

boot();
